/**
 * Ag-Grid 분석 프리셋 (SFR-005 데이터 분석 기능개발)
 *
 * 사용자가 컬럼을 일일이 그룹화·집계하지 않아도, 버튼 한 번으로 지역 담당자가 자주 보는 실적표가 나오게 한다.
 * 프리셋 = (행 그룹 컬럼 1개) + (집계 컬럼 N개) + (선택) 필터.
 *
 * 그룹·집계용 값은 원본 컬럼으로 만들기 어려운 것들(시간 구간, 신규/재위촉, 소외 포함 수요기관 구분 등)이라
 * 숨김 '분석용' 컬럼(getPresetColumns)을 따로 둔다. 평소에는 보이지 않고 프리셋을 적용할 때만 쓰인다.
 */
import { PROGRAM_OPTIONS } from 'utils/validation/programEditSpec'

const num = v => {
    const n = Number(v)
    return Number.isFinite(n) ? n : 0
}

// 그룹 행을 정해진 순서로 정렬하기 위한 비교기 (목록에 없는 값은 맨 뒤)
const orderComparator = order => (a, b) => {
    const ia = order.indexOf(a)
    const ib = order.indexOf(b)
    return (ia < 0 ? order.length : ia) - (ib < 0 ? order.length : ib)
}

/* ----------------------------- 구분 값 계산 ----------------------------- */

// 4시간 미만의 단기 교육은 실적으로 체크하지 않으므로 별도 구간으로 두고 프리셋에서 제외한다.
export const HOUR_BANDS = {
    short: '4시간 미만 (실적 제외)',
    mid: '4시간 이상 ~ 8시간 미만',
    long: '8시간 이상 ~ 50시간 미만',
    max: '50시간 이상',
}
const HOUR_BAND_ORDER = [HOUR_BANDS.mid, HOUR_BANDS.long, HOUR_BANDS.max, HOUR_BANDS.short]

const hourBandOf = data => {
    const h = num(data?.classHours)
    if (h < 4) return HOUR_BANDS.short
    if (h < 8) return HOUR_BANDS.mid
    if (h < 50) return HOUR_BANDS.long
    return HOUR_BANDS.max
}

const GRADE_ORDER = ['초등', '중등', '고등', '혼합', '미입력']
const gradeOf = data => data?.grade || '미입력'

// 수요기관: 일반학교/도서벽지/특수학교/지역아동센터/기타 + (일반·기타 중 소외인 경우) 일반+소외/기타+소외
const ORG_ORDER = ['일반학교', '도서벽지', '특수학교', '지역아동센터', '기타', '일반+소외', '기타+소외', '미입력']
const orgKindOf = data => {
    const type = data?.organizationType
    if (!type) return '미입력'
    if (data.isUnderserved && type === '일반학교') return '일반+소외'
    if (data.isUnderserved && type === '기타') return '기타+소외'
    return type
}

const EMPLOY_ORDER = ['신규', '재위촉']
const employKindOf = data => (data?.isEmployment ? '재위촉' : '신규')

const isBlank = v => v === undefined || v === null || String(v).trim() === ''

// 목록에 없는 값이 섞이는 그룹(월, 카테고리 등)은 값 순서대로 정렬하되 '미입력'은 맨 뒤로 보낸다.
const EMPTY = '미입력'
const emptyLastComparator = (a, b) => {
    if (a === b) return 0
    if (a === EMPTY) return 1
    if (b === EMPTY) return -1
    return String(a).localeCompare(String(b), 'ko')
}

// 'YYYY-MM-DD'(또는 ISO) → 'YYYY-MM'
const monthOf = v => (isBlank(v) ? EMPTY : String(v).slice(0, 7))

// 교육 유형 + 정규구분을 한 라벨로 (예: '센터교육 · 정규과정')
const typeKindOf = data => {
    const parts = [data?.educationType, data?.course].filter(v => !isBlank(v))
    return parts.length ? parts.join(' · ') : EMPTY
}

// 'YYYY-MM-DD'(또는 ISO) → 날짜 부분만
const ymdOf = v => (isBlank(v) ? '' : String(v).split('T')[0])

// 오늘 날짜(로컬 시간대) 'YYYY-MM-DD'
const todayYmd = () => {
    const t = new Date()
    return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`
}

// 보고 전에 확인할 만한 교육 데이터 상태. 빈 값뿐 아니라 값끼리 맞지 않거나 이상한 경우까지 본다.
// 한 교육에 문제가 여러 개면 해당하는 항목마다 각각 잡힌다(그룹별 건수의 합이 교육 건수보다 클 수 있다).
// (수료인원이 수강인원보다 많은 경우는 정상적으로 생길 수 있어 점검 대상에서 뺐다)
export const CHECK_KINDS = {
    ok: '정상',
    noPeriod: '교육 시작일·종료일 미입력',
    periodBadYear: '교육 시작일·종료일 연도 이상',
    periodReversed: '시작일이 종료일보다 늦음',
    periodTooLong: '교육기간이 1년 넘음',
    yearOutOfPeriod: '활동연도가 교육기간 밖',
    noApplicants: '수강인원 미입력',
    noGraduates: '수료인원 미입력',
    zero: '종료된 교육인데 수료인원 0명',
    sessionsOverHours: '교육횟수가 교육시간(교시)보다 많음',
    noHours: '교육시간 미입력·0',
    noOrg: '수요기관 구분 미입력',
    badOrg: '수요기관 구분이 선택 목록과 불일치',
    noGrade: '학년 미입력',
    badGrade: '학년이 선택 목록과 불일치',
    noCategory: '카테고리 미입력',
    badCategory: '카테고리가 선택 목록과 불일치',
    badType: '교육 유형·정규구분이 선택 목록과 불일치',
}
const CHECK_ORDER = Object.values(CHECK_KINDS)
// 해당하는 점검 항목을 전부 돌려준다(없으면 ['정상']). 한 교육이 여러 항목 그룹에 각각 잡힌다.
const checkKindsOf = data => {
    const K = CHECK_KINDS
    const found = []
    const start = ymdOf(data?.startDate)
    const end = ymdOf(data?.endDate)
    if (!start || !end) {
        found.push(K.noPeriod)
    } else {
        // 2262-08, 4613-12 처럼 오타로 보이는 연도
        const maxYear = new Date().getFullYear() + 2
        const badYear = ymd => {
            const y = Number(ymd.slice(0, 4))
            return y < 2000 || y > maxYear
        }
        if (badYear(start) || badYear(end)) {
            // 연도가 이상하면 역전·1년 초과·활동연도 점검은 같은 원인의 중복이라 건너뛴다
            found.push(K.periodBadYear)
        } else {
            if (start > end) found.push(K.periodReversed)
            if (new Date(`${end}T00:00:00`) - new Date(`${start}T00:00:00`) > 365 * 24 * 60 * 60 * 1000) found.push(K.periodTooLong)
            const year = Number(data?.registrationYear)
            if (year && (year < Number(start.slice(0, 4)) || year > Number(end.slice(0, 4)))) found.push(K.yearOutOfPeriod)
        }
    }

    if (isBlank(data?.numberOfApplicants)) found.push(K.noApplicants)
    if (isBlank(data?.numberOfGraduates)) found.push(K.noGraduates)
    // 아직 끝나지 않은 교육은 수료인원이 0명이어도 정상이라, 종료일이 오늘 이전인 교육만 본다
    else if (num(data.numberOfGraduates) === 0 && end && end < todayYmd()) found.push(K.zero)
    if (num(data?.numberOfSession) > num(data?.classHours) && num(data?.classHours) > 0) found.push(K.sessionsOverHours)
    if (num(data?.classHours) <= 0) found.push(K.noHours)
    if (isBlank(data?.organizationType)) found.push(K.noOrg)
    // '핵심거점', '찾아가는 교육(일반학교)' 처럼 선택 목록(5종)에 없는 값
    else if (!PROGRAM_OPTIONS.organizationType.includes(data.organizationType)) found.push(K.badOrg)
    if (isBlank(data?.grade)) found.push(K.noGrade)
    // '6-2', '특수학교' 처럼 선택 목록(4종)에 없는 값
    else if (!PROGRAM_OPTIONS.grade.includes(data.grade)) found.push(K.badGrade)
    if (isBlank(data?.category1)) found.push(K.noCategory)
    // 띄어쓰기·오타까지 포함해 선택 목록(14종)과 글자 그대로 같아야 정상
    else if (!PROGRAM_OPTIONS.category1.includes(data.category1)) found.push(K.badCategory)
    // 교육 유형·정규구분도 선택 목록과 글자 그대로 같아야 한다(예: '찾아오는 교육'). 비어 있는 값은 여기서 보지 않는다
    const outOfList = (v, list) => !isBlank(v) && !list.includes(v)
    if (outOfList(data?.educationType, PROGRAM_OPTIONS.educationType) || outOfList(data?.course, PROGRAM_OPTIONS.course)) found.push(K.badType)
    return found.length ? found : [K.ok]
}

// 생년월일 원본(YYMMDD 또는 뒤에 성별 숫자가 붙은 값) → { y, m, d } 또는 null(형식·날짜 오류)
// 7번째 자리가 있으면 세기를 정확히 알 수 있고, 없으면 올해 두 자리보다 크면 1900년대로 본다.
const parseBirth = raw => {
    const digits = String(raw ?? '').replace(/\D/g, '')
    if (digits.length !== 6 && digits.length !== 7 && digits.length !== 13) return null
    const yy = Number(digits.slice(0, 2))
    const m = Number(digits.slice(2, 4))
    const d = Number(digits.slice(4, 6))
    let century
    if (digits.length >= 7) {
        const g = Number(digits[6])
        if (g >= 1 && g <= 2) century = 1900
        else if (g >= 3 && g <= 4) century = 2000
        else if (g >= 5 && g <= 6) century = 1900
        else if (g >= 7 && g <= 8) century = 2000
        else return null
    } else {
        century = yy > new Date().getFullYear() % 100 ? 1900 : 2000
    }
    const y = century + yy
    const dt = new Date(y, m - 1, d)
    if (dt.getFullYear() !== y || dt.getMonth() !== m - 1 || dt.getDate() !== d) return null
    return { y, m, d }
}

// 강사 정보 점검: 빈 값뿐 아니라 값끼리 맞지 않거나 형식이 이상한 경우까지 본다.
// 한 강사에 문제가 여러 개면 해당하는 항목마다 각각 잡힌다(그룹별 인원의 합이 강사 수보다 클 수 있다).
export const TUTOR_CHECK_KINDS = {
    ok: '정상',
    noPeriod: '계약 시작일·만료일 미입력',
    periodReversed: '계약 시작일이 만료일보다 늦음',
    yearOutOfPeriod: '활동연도가 계약기간 밖',
    noContact: '연락처 미입력',
    badContact: '연락처 형식 이상',
    noEmail: '이메일 미입력',
    badEmail: '이메일 형식 이상',
    noEmployType: '활동형태 미입력',
    noGender: '성별 미입력',
    badBirth: '생년월일 형식 이상',
    careerGapMale: '경력단절여성인데 성별이 남',
    cohortAfterYear: '기수가 활동연도보다 늦음',
}
const TUTOR_CHECK_ORDER = Object.values(TUTOR_CHECK_KINDS)

// 해당하는 점검 항목을 전부 돌려준다(없으면 ['정상']). 한 강사가 여러 항목 그룹에 각각 잡힌다.
const tutorCheckKindsOf = data => {
    const K = TUTOR_CHECK_KINDS
    const found = []
    const start = ymdOf(data?.startDate)
    const end = ymdOf(data?.endDate)
    const year = Number(data?.registrationYear)
    if (!start || !end) {
        found.push(K.noPeriod)
    } else {
        if (start > end) found.push(K.periodReversed)
        if (year && (year < Number(start.slice(0, 4)) || year > Number(end.slice(0, 4)))) found.push(K.yearOutOfPeriod)
    }

    if (isBlank(data?.contact)) found.push(K.noContact)
    else if (!/^0\d{9,10}$/.test(String(data.contact).replace(/\D/g, ''))) found.push(K.badContact)
    if (isBlank(data?.email)) found.push(K.noEmail)
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(data.email).trim())) found.push(K.badEmail)
    if (isBlank(data?.employmentType)) found.push(K.noEmployType)
    if (isBlank(data?.gender)) found.push(K.noGender)
    if (!parseBirth(data?.residentNum)) found.push(K.badBirth)
    if (data?.isCareerGapFemale && data?.gender === '남') found.push(K.careerGapMale)

    const cohort = Number(data?.cohortStartYear)
    if (cohort && year && cohort > year) found.push(K.cohortAfterYear)
    return found.length ? found : [K.ok]
}

/* ----------------------------- 분석용 숨김 컬럼 ----------------------------- */

// 분석 컬럼 colId. 프리셋 정의와 컬럼 정의가 같은 이름을 쓰도록 상수로 둔다.
const C = {
    employKind: 'presetEmployKind',
    headcount: 'presetHeadcount',
    share: 'presetShare',
    hourBand: 'presetHourBand',
    grade: 'presetGrade',
    orgKind: 'presetOrgKind',
    programs: 'presetPrograms',
    hours: 'presetHours',
    graduates: 'presetGraduates',
    startMonth: 'presetStartMonth',
    category: 'presetCategory',
    typeKind: 'presetTypeKind',
    checkKind: 'presetCheckKind',
    tutorCheckKind: 'presetTutorCheckKind',
    endMonth: 'presetEndMonth',
    area: 'presetArea',
}

const base = { hide: true, editable: false, enableRowGroup: true, suppressColumnsToolPanel: false }

const groupCol = (colId, headerName, valueGetter, order, extra = {}) => ({
    ...base,
    colId,
    headerName: `[분석] ${headerName}`,
    valueGetter: p => (p.data ? valueGetter(p.data, p) : undefined),
    ...(order ? { comparator: orderComparator(order) } : {}),
    ...extra,
})

const sumCol = (colId, headerName, valueGetter, extra = {}) => ({
    ...base,
    colId,
    headerName,
    enableRowGroup: false,
    enableValue: true,
    filter: false,
    floatingFilter: false,
    aggFunc: 'sum',
    valueGetter: p => (p.data ? valueGetter(p.data) : undefined),
    ...extra,
})

// 전체 대비 비율(%) — 그룹 행 값을 현재 조회 중인 전체 건수로 나눈다.
const shareCol = () =>
    sumCol(C.share, '비율(%)', () => 1, {
        valueFormatter: p => {
            const total = p.context?.getPresetTotal?.() || 0
            if (!total || p.value == null) return ''
            return `${((p.value / total) * 100).toFixed(1)}%`
        },
    })

const TUTOR_COLUMNS = [
    groupCol(C.employKind, '신규/재위촉', employKindOf, EMPLOY_ORDER),
    groupCol(C.endMonth, '계약만료 월', d => monthOf(d.endDate), null, { comparator: emptyLastComparator }),
    groupCol(C.tutorCheckKind, '데이터 점검', d => d.presetReason ?? tutorCheckKindsOf(d)[0], TUTOR_CHECK_ORDER, { filter: 'agSetColumnFilter' }),
    groupCol(C.area, '권역', d => (isBlank(d.areaName) ? EMPTY : d.areaName), null, { comparator: emptyLastComparator }),
    sumCol(C.headcount, '강사 수(명)', () => 1),
    shareCol(),
]

const PROGRAM_COLUMNS = [
    groupCol(C.hourBand, '교육시간 구간', d => hourBandOf(d), HOUR_BAND_ORDER, {
        filter: 'agSetColumnFilter',
    }),
    groupCol(C.grade, '학년', gradeOf, GRADE_ORDER),
    groupCol(C.orgKind, '수요기관', orgKindOf, ORG_ORDER),
    groupCol(C.startMonth, '교육 시작 월', d => monthOf(d.startDate), null, { comparator: emptyLastComparator }),
    groupCol(C.category, '카테고리', d => (isBlank(d.category1) ? EMPTY : d.category1), null, {
        comparator: emptyLastComparator,
    }),
    groupCol(C.typeKind, '교육 유형·정규구분', typeKindOf, null, { comparator: emptyLastComparator }),
    groupCol(C.checkKind, '데이터 점검', d => d.presetReason ?? checkKindsOf(d)[0], CHECK_ORDER, { filter: 'agSetColumnFilter' }),
    sumCol(C.programs, '교육 건수', () => 1),
    sumCol(C.hours, '총 교육시간(시간)', d => num(d.classHours)),
    sumCol(C.graduates, '수혜(수료)학생 수(명)', d => num(d.numberOfGraduates)),
]

export const getPresetColumns = pageType => {
    if (pageType === 'tutor') return TUTOR_COLUMNS
    if (pageType === 'program') return PROGRAM_COLUMNS
    return []
}

/* ----------------------------- 프리셋 정의 ----------------------------- */

/**
 * detail: 펼친 개별 행에서 보여 줄 컬럼(아이디·이름) / newRow: 버튼을 새 줄에서 시작 / subGroup: 전체 지역을 볼 때 두 번째로 나눌 컬럼 / group: 행 그룹 컬럼 / values: 보여줄 집계 컬럼 / filter: 적용할 필터 모델 / note: 화면 안내 문구
 */
const PRESETS = {
    tutor: [
        {
            key: 'tutorNewRatio',
            label: '신규/재위촉 강사 비율',
            group: C.employKind,
            groupHeader: '구분',
            values: [C.headcount, C.share],
            note: '선택한 연도에 활동한 강사를 신규와 재위촉으로 나눈 인원과 비율입니다.',
        },
        {
            key: 'tutorContractEnd',
            label: '계약만료 월별 강사',
            group: C.endMonth,
            groupHeader: '계약만료 월',
            values: [C.headcount, C.share],
            note: '계약만료일 기준 월별 강사 수입니다. 재위촉 대상을 미리 파악할 때 사용합니다.',
        },
        {
            key: 'tutorArea',
            label: '권역별 강사 분포',
            group: C.area,
            groupHeader: '권역',
            values: [C.headcount, C.share],
            note: '강사 정보에 등록된 권역별 인원과 비율입니다. 권역이 비어 있으면 미입력으로 모입니다.',
        },
        {
            key: 'tutorCheck',
            label: '강사 데이터 점검',
            group: C.tutorCheckKind,
            groupHeader: '점검 항목',
            values: [C.headcount],
            filter: {
                [C.tutorCheckKind]: {
                    filterType: 'set',
                    values: Object.values(TUTOR_CHECK_KINDS).filter(v => v !== TUTOR_CHECK_KINDS.ok),
                },
            },
            // 한 강사에 문제가 여러 개면 해당하는 항목마다 한 줄씩 복제해서 각 그룹에 모두 넣는다
            explodeRows: rows =>
                rows.flatMap((d, i) =>
                    tutorCheckKindsOf(d).map(kind => ({ ...d, presetReason: kind, presetRowKey: `${i}|${kind}` })),
                ),
            note: '오류 데이터로 판단되는 강사입니다.\u00a0확인 후 수정이 필요합니다. 한 강사에 문제가 여러 개면 항목마다 집계되어 합계가 강사 수보다 클 수 있습니다.',
        },
    ],
    program: [
        {
            key: 'programHourBand',
            label: '4/8/50시간별 수혜학생',
            group: C.hourBand,
            groupHeader: '교육시간 구간',
            values: [C.programs, C.hours, C.graduates],
            filter: {
                [C.hourBand]: {
                    filterType: 'set',
                    values: [HOUR_BANDS.mid, HOUR_BANDS.long, HOUR_BANDS.max],
                },
            },
            note: '4시간 미만의 단기 교육은 실적으로 체크하지 않아 제외했습니다.',
        },
        {
            key: 'programGrade',
            label: '학년별 교육시간·수혜학생',
            group: C.grade,
            groupHeader: '학년',
            values: [C.programs, C.hours, C.graduates],
            note: '초등/중등/고등/혼합별 총 교육시간과 수혜(수료)학생 수입니다. 비어 있으면 미입력, 선택 목록에 없는 값은 따로 모이니 교육 데이터 점검에서 확인해 주세요.',
        },
        {
            key: 'programOrg',
            label: '수요기관별 교육시간·수혜학생',
            group: C.orgKind,
            groupHeader: '수요기관',
            values: [C.programs, C.hours, C.graduates],
            note: '일반학교/도서벽지/특수학교/지역아동센터/기타와, 소외로 등록된 일반·기타(일반+소외, 기타+소외)별 집계입니다. 선택 목록에 없는 값은 따로 모이니 교육 데이터 점검에서 확인해 주세요.',
        },
        {
            key: 'programMonth',
            newRow: true, // 지역 요청 항목 아래 줄부터 추가 제공 항목
            label: '월별 교육시간·수혜학생',
            group: C.startMonth,
            groupHeader: '교육 시작 월',
            values: [C.programs, C.hours, C.graduates],
            note: '교육 시작일 기준 월별 집계입니다. 성과분석의 월별 차트는 교육 건수만 보여 주므로 시간과 수혜학생을 함께 봅니다. 시작일이 비어 있으면 미입력으로 모이고, 연도가 잘못된 날짜(예: 2262-08)는 따로 모이니 교육 데이터 점검에서 확인해 주세요.',
        },
        {
            key: 'programCategory',
            label: '카테고리별 교육시간·수혜학생',
            group: C.category,
            groupHeader: '카테고리',
            values: [C.programs, C.hours, C.graduates],
            note: '카테고리1(과목) 기준 집계입니다. 비어 있으면 미입력으로 모이고, 선택 목록(14종)과 띄어쓰기·오타가 다른 값은 따로 모이니 교육 데이터 점검에서 확인해 주세요.',
        },
        {
            key: 'programType',
            label: '교육 유형·정규구분별',
            group: C.typeKind,
            groupHeader: '교육 유형·정규구분',
            values: [C.programs, C.hours, C.graduates],
            note: '센터교육/찾아가는 교육/기타와 정규과정/비정규과정을 묶어 본 집계입니다. 선택 목록에 없는 값은 따로 모이니 교육 데이터 점검에서 확인해 주세요.',
        },
        {
            key: 'programCheck',
            label: '교육 데이터 점검',
            group: C.checkKind,
            groupHeader: '점검 항목',
            values: [C.programs, C.graduates],
            filter: {
                [C.checkKind]: {
                    filterType: 'set',
                    values: Object.values(CHECK_KINDS).filter(v => v !== CHECK_KINDS.ok),
                },
            },
            // 한 교육에 문제가 여러 개면 해당하는 항목마다 한 줄씩 복제해서 각 그룹에 모두 넣는다
            explodeRows: rows =>
                rows.flatMap((d, i) =>
                    checkKindsOf(d).map(kind => ({ ...d, presetReason: kind, presetRowKey: `${i}|${kind}` })),
                ),
            // 첫 두 문장은 한 줄로 붙여 두려고 마침표 뒤를 줄바꿈 대상이 아닌 공백(\u00a0)으로 잇는다(getPresets 가 '. ' 만 줄바꿈)
            note: '오류 데이터로 판단되는 프로그램입니다.\u00a0확인 후 수정이 필요합니다. 한 교육에 문제가 여러 개면 항목마다 집계되어 합계가 교육 건수보다 클 수 있습니다.',
        },
    ],
}

// 모든 프리셋은 전체 지역을 볼 때(관리자) 지역별로도 한 단계 더 나뉜다. applyPreset 이 지역이 여러 개일 때만 적용한다.
// 그룹을 펼쳤을 때 개별 건을 알아볼 수 있게 아이디와 이름(제목)은 모든 프리셋에서 보여 준다.
const DETAIL_COLUMNS = {
    tutor: ['tutorId', 'tutorName'],
    program: ['programId', 'programName'],
}
export const getPresets = pageType =>
    (PRESETS[pageType] || []).map(p => ({
        subGroup: 'regionId',
        detail: DETAIL_COLUMNS[pageType] || [],
        ...p,
        // 문장마다 줄을 바꿔 보여 준다(.note 의 white-space: pre-line)
        note: p.note.replace(/\. /g, '.\n'),
    }))

/* ----------------------------- 적용 / 해제 ----------------------------- */

/** 프리셋을 그리드에 적용한다. 기존 필터·그룹·집계 상태는 프리셋 기준으로 덮어쓴다. */
// 현재 그리드에 지역이 둘 이상 섞여 있는지(전체 지역을 보는 관리자 계정인지)
const hasMultipleRegions = api => {
    const regions = new Set()
    api.forEachNode(node => {
        if (!node.group && node.data?.regionId) regions.add(node.data.regionId)
    })
    return regions.size > 1
}

export const applyPreset = (api, preset) => {
    // subGroup 이 있는 프리셋은 전체 지역을 볼 때만 한 단계 더 지역별로 나눈다(자기 지역만 보는 계정은 의미 없음)
    const subGroup = preset.subGroup && hasMultipleRegions(api) ? preset.subGroup : null
    api.setFilterModel(preset.filter || null)
    api.setGridOption('autoGroupColumnDef', { headerName: preset.groupHeader, minWidth: 240, pinned: 'left' })
    api.setGridOption('suppressAggFuncInHeader', true)
    api.setGridOption('grandTotalRow', 'bottom')
    api.applyColumnState({
        state: [
            { colId: preset.group, rowGroup: true, rowGroupIndex: 0, hide: true, sort: 'asc' },
            ...(subGroup ? [{ colId: subGroup, rowGroup: true, rowGroupIndex: 1, hide: true, sort: 'asc' }] : []),
            // 아이디·이름은 그룹 행에는 비어 있고, 그룹을 펼친 개별 행에서만 값이 보인다
            ...(preset.detail || []).map(colId => ({ colId, hide: false })),
            ...preset.values.map(colId => ({
                colId,
                hide: false,
                aggFunc: 'sum',
            })),
        ],
        defaultState: { hide: true, rowGroup: false, aggFunc: null, sort: null, pivot: false },
        applyOrder: false,
    })
    api.setGridOption('groupDefaultExpanded', 0)
}

/** 프리셋을 걷어내고 컬럼 정의의 초기 상태로 되돌린다. */
export const resetPreset = api => {
    api.setFilterModel(null)
    api.setGridOption('autoGroupColumnDef', undefined)
    api.setGridOption('suppressAggFuncInHeader', false)
    api.setGridOption('grandTotalRow', undefined)
    api.resetColumnState()
}

/** 필터를 통과한 일반(리프) 행 수 — 비율 계산의 분모 */
export const countLeafRows = api => {
    let count = 0
    api.forEachNodeAfterFilter(node => {
        if (!node.group) count += 1
    })
    return count
}
